import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MagnifyingGlass, UserCircle, SealCheck } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { careerApi, AVAILABILITY_STATES } from "@/lib/jobsApi";
import { toast } from "sonner";

/** Candidate search — employer view. */
export default function Candidates() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [skill, setSkill] = useState("");
  const [availability, setAvailability] = useState("");
  const [location, setLocation] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 30 };
      if (q.trim()) params.q = q.trim();
      if (skill.trim()) params.skill = skill.trim();
      if (availability) params.availability = availability;
      if (location.trim()) params.location = location.trim();
      const d = await careerApi.search(params);
      setItems(d.items || []);
      setTotal(d.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Search failed"); }
    finally { setLoading(false); }
  }, [q, skill, availability, location]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-4" data-testid="candidates-page">
      <button onClick={() => navigate("/jobs")} className="text-white/60 hover:text-white flex items-center gap-1 text-sm">
        <ArrowLeft size={14} weight="bold" /> Back to Jobs
      </button>
      <h1 className="font-heading text-3xl md:text-4xl font-black">Find candidates</h1>

      <Card className="bg-white/[0.03] border-white/10">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" weight="bold" />
            <Input data-testid="cand-search" placeholder="Headline, summary, keyword…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white/5 border-white/10" />
          </div>
          <Input data-testid="cand-skill" placeholder="Skill (e.g. python)" value={skill} onChange={(e) => setSkill(e.target.value)} className="bg-white/5 border-white/10" />
          <Input data-testid="cand-location" placeholder="Location" value={location} onChange={(e) => setLocation(e.target.value)} className="bg-white/5 border-white/10" />
          <div className="md:col-span-4">
            <Select value={availability || "__all"} onValueChange={(v) => setAvailability(v === "__all" ? "" : v)}>
              <SelectTrigger data-testid="cand-availability" className="bg-white/5 border-white/10 max-w-xs"><SelectValue placeholder="Availability" /></SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                <SelectItem value="__all">Any availability</SelectItem>
                {AVAILABILITY_STATES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-white/50">{loading ? "Searching…" : `${total} candidate${total === 1 ? "" : "s"}`}</div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((s) => (
          <Card
            key={s.user_id}
            data-testid={`cand-${s.user_id}`}
            className="bg-white/[0.03] border-white/10 cursor-pointer hover:bg-white/[0.06] transition"
            onClick={() => navigate(`/career/${s.user_id}`)}
          >
            <CardContent className="p-4 flex items-start gap-3">
              {s.photo || s.user?.picture ? (
                <img src={s.photo || s.user?.picture} alt="" className="w-14 h-14 rounded-full object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center">
                  <UserCircle size={30} className="text-white/40" weight="duotone" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading font-bold">{s.user?.name || "Candidate"}</h3>
                  {s.user?.employer_verified && <SealCheck size={14} className="text-blue-400" weight="fill" />}
                </div>
                {s.headline && <p className="text-sm text-white/70 line-clamp-1">{s.headline}</p>}
                {s.skills?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {s.skills.slice(0, 5).map((sk) => <span key={sk} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-white/60">{sk}</span>)}
                    {s.skills.length > 5 && <span className="text-[10px] text-white/40">+{s.skills.length - 5}</span>}
                  </div>
                )}
                <div className="mt-2 text-[10px] uppercase tracking-widest text-emerald-300 font-bold">
                  {AVAILABILITY_STATES.find((a) => a.value === s.availability)?.label}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {!loading && items.length === 0 && (
        <div className="text-center py-16 text-white/40">No candidates found. Try adjusting your filters.</div>
      )}
    </div>
  );
}
